<template>
  <div class="map-wrapper">
    <KMap
      :key="`map-${org?.orgId}`"
      ref="map"
      v-model:zoom="zoom"
      v-model:marker-coordinates="markerCoordinates"
      :extent="initialExtent"
      :basemaps="filteredBasemaps"
      :layers="props.layers"
      :marker-enable="enableMarker"
      :click-enable="enableMarker"
      :theme="theme"
      :trim-layers="organizationAreaTrimmer"
      @ready="mapReady">
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}" />
      </template>

      <template #widget-bottom-left>
        <KBasemapSelector :selected-basemap="selectedBasemap" />
      </template>

      <template #widget-top-left>
        <div v-if="!webGLSupported">
          <v-alert :text="$t('messages.error.webglNotSupported')" type="warning" rounded max-width="480px"></v-alert>
        </div>
      </template>

      <template #widget-top-right>
        <ToolsToc :initial-extent="initialExtent" @show-map-legend="showMapLegend = !showMapLegend"></ToolsToc>
        <KSimpleLayerManager v-if="favorites.length" :white-list="favorites" :hide-disabled="false" />
      </template>

      <template #widget-bottom-right>
        <div class="legend-logo-combo">
          <KLegend v-show="showMapLegend" style="min-width: 250px !important" collapsable>
            <template #legend:elevation_layer:wfas:SouthAmerica_30arcsec_DEM_GMTED2010_Masked="{ legend }">
              <v-list class="elevation_ramp">
                <v-list-subheader class="legend-subheader">
                  <span>{{ translateLangsKeyObject(legend.title) }}</span>
                </v-list-subheader>
                <KLegendItem
                  :legend-with-ids="asLegendWithLayer(legend)"
                  :item="asUniqueItem(legend.items[0])"
                  :unique-item="false"></KLegendItem>
                <KLegendItemRamp :item="asRampItem(legend.items[1])"></KLegendItemRamp>
              </v-list>
            </template>
          </KLegend>
          <div class="logo-stack">
            <TenantBrandLogos v-if="customerExtraLogos.length" :logos="customerExtraLogos" />
            <KLogo :logo="customerLogo" />
          </div>
        </div>
      </template>

      <template #[`${kTimePlayerSlotName}`]>
        <KTimePlayer
          v-if="hasActiveTemporalLayer"
          loop
          :class="{ 'w-100': isTooSmallWidth }"
          :black-list="BLACK_LIST_CUSTOM_TEMPORAL_LAYERS"
          :step="0" />
      </template>

      <!-- Between the side panels (not full-bottom, which runs under them), below the time player -->
      <template #stretch-bottom-menu>
        <MapAttribution />
      </template>

      <template #[`${kSearchSlotName}`]>
        <KSearch id="search" rounded enable-my-position :search-targets="searchTargets" :class="isTooSmallWidth ? 'w-100' : 'searchBar'" />
      </template>

      <KMeasureExtended
        v-if="measure"
        :position="{ x: 400, y: 0 }"
        draggable
        closeable
        :length-units="['Metric System', 'meters', 'kilometers']"
        :area-units="['Metric System', 'square-meters', 'square-kilometers']"
        @close="measure = false"></KMeasureExtended>

      <MapPopups />
    </KMap>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, toRef } from 'vue'

// Karonte import

//Navigation Import

//Store import
// Constants import

import { useRoute } from 'vue-router'

import { storeToRefs } from 'pinia'

import { asyncComputed, useStorage } from '@vueuse/core'
import { useDisplay } from 'vuetify'

import { KMap } from '@tsyl/karonte'
import useLayers from '@tsyl/karonte/lib/composables/useLayers'
import type { ClickEvent } from '@tsyl/karonte/lib/types/events'
import type { Layer } from '@tsyl/karonte/lib/types/layers'
import type { LegendItem, LegendItemRamp } from '@tsyl/karonte/lib/types/legend'
import type { ExtentCommon, View } from '@tsyl/karonte/lib/types/view'

// Translator import
import karonteI18n from '@tsyl/karonte/lib/utils/i18n'
import type { ILayerWithFeatures } from '@tsyl/karonte/lib/wrappers/LayerInterfaces'
import { KLegendItem, KLegendItemRamp } from '@tsyl/karonte-components'
import type { KLegendLegend, KLegendLegendItem, LegendWithLayer } from '@tsyl/karonte-components/lib/types/KLegendElement'
import { KBasemapSelector, KTimePlayer } from '@tsyl/karonte-widgets'
import type { KResult, KSearchArgs } from '@tsyl/karonte-widgets/lib/types/KSearch'
import type { RGeocoderResult } from '@tsyl/radon/lib/types/geocoder'

import MapAttribution from '@/components/MapAttribution.vue'
import MapPopups from '@/components/MapPopups.vue'
import TenantBrandLogos from '@/components/TenantBrandLogos.vue'
import ToolsToc from '@/components/Tools/ToolsToc.vue'
import { useOrganizationAreaTrimmer } from '@/composables/useOrganizationAreaTrimmer'
import { getAvailableBasemapIds } from '@/constants/basemapsByCustomer'
import { heatDetectionLayerIds } from '@/constants/layers/hotspots/LivingAtlasHeatDetection'
import { LEFT_PANNEL_WIDTH, RIGHT_PANNEL_WIDTH } from '@/constants/layout'
import customers from '@/customers/customers'
import { getUserAccessLevel } from '@/services/api/user'
import { computeCustomArea } from '@/services/areas'
import AuthStore from '@/stores/auth.ts'
import favoritesStore from '@/stores/favoritesStore'
import FiltersStore from '@/stores/FiltersStore'
import MapToolsStore from '@/stores/mapToolsStore'
import MarkerStore from '@/stores/markerStore'
import settingsStore from '@/stores/settingsStore.ts'
import type { BrandLogo } from '@/types/Customer'
import { worldExtent } from '@/utils/mapOrganizationTool.ts'

import basemaps from '../constants/basemaps'
const karonteI18nObject = karonteI18n(`sa`, {})
const { translateLangsKeyObject } = karonteI18nObject

const props = defineProps<{ layers: Layer[] }>()

// The elevation legend slots always provide a unique item followed by a ramp item
// The legend slot only provides the legend; KLegendItem also needs the ids of the layer it belongs to
const asLegendWithLayer = (legend: KLegendLegend): LegendWithLayer => ({
  legend,
  layerId: 'elevation_layer',
  sublayerId: 'wfas:SouthAmerica_30arcsec_DEM_GMTED2010_Masked'
})
const asUniqueItem = (item: KLegendLegendItem) => item as LegendItem & { index: number }
const asRampItem = (item: KLegendLegendItem) => item as unknown as LegendItemRamp
const showMapLegend = ref(false)

const emit = defineEmits<{
  (e: 'viewLoaded', v: View): void
}>()

const map = ref()
const customerLogo = ref('technosylva')
const customerExtraLogos = ref<BrandLogo[]>([])

const BLACK_LIST_CUSTOM_TEMPORAL_LAYERS = [
  'EffisModis1',
  'EffisModis7',
  'EffisModis30',
  'EffisViirs1',
  'EffisViirs7',
  'EffisViirs30',
  'BurntAreasViirsEffis1',
  'BurntAreasViirsEffis7',
  'BurntAreasViirsEffis30'
]

const { waitUntilInitialised } = AuthStore()
const filtersStore = FiltersStore()
const { filters, searchText } = storeToRefs(filtersStore)
const { theme } = storeToRefs(settingsStore())

const authStore = AuthStore()
const { isAuthenticated, org } = storeToRefs(authStore)

const filteredBasemaps = computed(() => {
  const availableIds = getAvailableBasemapIds(org.value?.orgId)
  return basemaps.filter((b) => b.id !== undefined && availableIds.includes(b.id))
})

// Map tools menu
const mapToolsStore = MapToolsStore()
const { measure } = storeToRefs(mapToolsStore)
const markerStore = MarkerStore()
const { markerCoordinates, enableMarker, zoom } = storeToRefs(markerStore)

// const { updateQueryValue } = useQueryNavigation()

const FavoritesStore = favoritesStore()
const { favorites } = storeToRefs(FavoritesStore)
const selectedBasemap = ref(useStorage('selectedBasemap', 'imageryHybrid'))

const { asyncGetLayerById, allLayers } = useLayers()

const hasActiveTemporalLayer = computed(() => {
  return allLayers.value?.some((layer) => layer.hasTimeDimension.value && layer.active.value) ?? false
})

const initialExtent = computed<ExtentCommon>(() => {
  return org?.value?.extent || worldExtent
})

const organizationArea = ref<string | undefined>(undefined)
// Every layer is trimmed to the organization area except the heat detection ones, which
// are already filtered by the feature service.
const organizationAreaTrimmer = useOrganizationAreaTrimmer(
  toRef(() => props.layers),
  organizationArea,
  heatDetectionLayerIds
)

async function mapClickIncident(event: ClickEvent) {
  // const layersSelected = await event.getFeatures()
  // const incidentSelected = layersSelected.find((item) => item.layer)
  // if (incidentSelected?.attributes.id) {
  //   const idSelected = incidentSelected?.attributes.id
  //   await updateQueryValue("incident", idSelected)
  // }
}

const isMapReady = ref(false)
function mapReady() {
  isMapReady.value = true
  const view = map.value.view as View
  emit('viewLoaded', view)
  map.value.view.addCallback('click', mapClickIncident)
}

const { width } = useDisplay()
const route = useRoute()
const isLeftPannelOpen = computed(() => {
  return route.name !== 'default'
})

const isRightPannelOpen = computed(() => {
  return Boolean(route.query.incident)
})
const isTooSmallWidth = computed(() => {
  let maxWidth = 1600
  if (!isLeftPannelOpen.value) maxWidth -= LEFT_PANNEL_WIDTH
  if (!isRightPannelOpen.value) maxWidth -= RIGHT_PANNEL_WIDTH
  return width.value < maxWidth
})

const kTimePlayerSlotName = computed(() => {
  return isTooSmallWidth.value ? 'widget-stretch-bottom' : 'widget-bottom-middle'
})

const kSearchSlotName = computed(() => {
  return isTooSmallWidth.value ? 'widget-stretch-top' : 'widget-top-middle'
})

const searchTargets: KSearchArgs[] = [
  {
    count: 2, // Maximum 2 results in this search target
    type: 'geocoder', // A geocoder search
    goTo: { type: 'extent' }, // If a result from this group is selected, the view will zoom to its extent
    textBuilder: (result: KResult) => (result as Partial<RGeocoderResult<Record<string, unknown>>>).name ?? '', // Call back to parse the result text
    resultsId: 0
  },
  {
    id: 'incidents_layer', // Id of the layer
    property: 'name', // Property of the layer features that will be used for the search
    count: 2, // Maximum 2 results in this search target
    type: 'layer', // A layer search
    icon: 'mdi-fire', // Icon used in the results item
    goTo: { type: 'coordinates', zoom: 16 }, // If a result from this group is selected, the view will zoom to its extent
    debounceTime: 1000,
    resultsId: 1
  }
]

function checkWebGLSupport() {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    return !!gl
  } catch (e) {
    console.warn('WebGL availability check threw', e)
    return false
  }
}

const webGLSupported = ref(true)

const incidentsLayer = asyncComputed(async () => {
  return await asyncGetLayerById<Layer & ILayerWithFeatures>('incidents_layer')
}, undefined)

onMounted(() => {
  if (!checkWebGLSupport()) {
    webGLSupported.value = false
  }

  // Load layers that may require auth
  waitUntilInitialised().then(() => {
    // TODO: Uncomment to review functionality
    // addLayers(incidentLayer)
  })

  watch(
    [isMapReady, isAuthenticated],
    ([mapReady, authenticated]) => {
      if (mapReady && authenticated) {
        const view: View = map.value.view as View
        const extent: ExtentCommon = org?.value?.extent || worldExtent
        view.goToExtent(extent)
      }
    },
    {
      immediate: true
    }
  )

  watch(
    isAuthenticated,
    async (authenticated) => {
      if (authenticated) {
        // TRIMMER
        let trimmer: string | undefined = undefined
        trimmer = computeCustomArea(await getUserAccessLevel())
        if (trimmer && org.value?.trimLayers) {
          organizationArea.value = trimmer
        } else {
          organizationArea.value = undefined
        }

        // LOGO
        const customer = customers.find((c) => c.orgId === org.value?.orgId)
        customerLogo.value = customer?.logo || 'technosylva'
        customerExtraLogos.value = customer?.extraLogos ?? []
      }
    },
    {
      immediate: true
    }
  )
})

watch([filters, searchText], () => {
  incidentsLayer.value?.refresh()
})
</script>

<style scoped lang="scss">
.map-wrapper {
  height: 100vh;
}
.searchBar {
  width: 300px;
}
:deep(.v-field__append-inner) {
  margin-right: 3px !important;
}
:deep(.legend-icon) {
  font-size: 30px;
}

:deep(.simple-layer-manager) {
  width: 30px !important;
  padding: 0 !important;
}

.elevation_ramp::v-deep(.ramp-column) {
  height: 300px !important;
}

:deep(.unselected-layer) {
  color: rgb(var(--v-theme-on-background)) !important;
}

/*
.test::v-deep(.ramp-labels) {
  font-size: 2px !important;
}

.test::v-deep(.ramp-label) {
  font-size: 16px !important;
}
*/

.legend-logo-combo {
  display: flex;
  flex-direction: column;
  align-items: end;
  justify-content: end;
  pointer-events: none;
  height: 100%;
  :deep(.widget-wrapper) > * {
    pointer-events: all;
  }
}

// Brand logos + company logo as a self-contained, centered unit in the corner.
.logo-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
